/**
 * JSM Land use-case onboarding experiment (jsmgrowth_519_jsm_land_use_case_onboarding)
 *
 * Variant-only card grid: shows 6 team types upfront, with the remaining
 * 8 collapsed behind a "+ Show 8 more options" button.
 *
 * This component is ONLY rendered for the `variant` cohort of the experiment.
 * The original CardsGrid is used for all other cohorts — it is NOT modified.
 */
import { Fragment, useCallback, useMemo, useState } from 'react';

import { css, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Grid, media, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';

import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import {
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	UserSegmentationTeamTypes,
} from '../../models/user-segmentation.ts';

import { UserSegCard } from './card.tsx';
import { CardsMapObject, useCardsMap } from './cards-grid.tsx';
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from './constants';

const messages = defineMessages({
	showMoreOptions: {
		id: 'UserSeg.teamTypeSelection.jsmUseCase.showMore',
		defaultMessage: 'Show {count} more options',
	},
});

const JSM_USE_CASE_COLLAPSED_TEAM_TYPES: readonly UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
] as const;

const JSM_USE_CASE_ALL_TEAM_TYPES: readonly UserSegmentationTeamTypes[] = [
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
] as const;

const getCardsForTeamTypes = (
	cardsMap: CardsMapObject[],
	teamTypes: readonly UserSegmentationTeamTypes[],
) =>
	teamTypes
		.map((teamType) => cardsMap.find((card) => card.teamType === teamType))
		.filter((card): card is CardsMapObject => card !== undefined);

export const JsmUseCaseCardsGrid = () => {
	const intl = useIntl();
	const cardsMap = useCardsMap();
	const {
		userSegmentationContext: { isTeamTypesExpanded, selectedTeamType, setIsTeamTypesExpanded },
	} = useConfirmationPage();
	const isPlaygroundSignup = useMemo(() => isJsmPlaygroundSignup(window.location.search), []);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [cardSelected, setCardSelected] = useState(false);
	const [shouldAnimateExpandedCards, setShouldAnimateExpandedCards] = useState(false);

	const onCardClick = useCallback(() => {
		setCardSelected(true);
	}, []);
	const afterCardClick = useCallback(() => {
		setCardSelected(false);
	}, []);

	const cards = getCardsForTeamTypes(
		cardsMap,
		isTeamTypesExpanded ? JSM_USE_CASE_ALL_TEAM_TYPES : JSM_USE_CASE_COLLAPSED_TEAM_TYPES,
	);
	const hiddenCardCount =
		JSM_USE_CASE_ALL_TEAM_TYPES.length - JSM_USE_CASE_COLLAPSED_TEAM_TYPES.length;

	const showMoreText = intl.formatMessage(messages.showMoreOptions, {
		count: hiddenCardCount,
	});

	return (
		<Box xcss={contentWrapperStyles}>
			<Grid xcss={gridContainerBase} rowGap="space.200" columnGap="space.300">
				{cards.map((cardObject, index) => {
					const shouldAnimateCard =
						shouldAnimateExpandedCards && index >= JSM_USE_CASE_COLLAPSED_TEAM_TYPES.length;
					const card = (
						<UserSegCard
							idx={index}
							{...cardObject}
							onClick={onCardClick}
							afterClick={afterCardClick}
							disabled={cardSelected}
							shouldFitContainer
							isPreselected={isPlaygroundSignup && cardObject.teamType === selectedTeamType}
						/>
					);

					return shouldAnimateCard ? (
						<div
							key={cardObject.teamType}
							css={revealCardStyles}
							data-testid="animated-team-type-card"
						>
							{card}
						</div>
					) : (
						<Fragment key={cardObject.teamType}>{card}</Fragment>
					);
				})}
			</Grid>
			{!isTeamTypesExpanded && (
				<button
					css={showMoreButtonStyles}
					onClick={() => {
						setShouldAnimateExpandedCards(true);
						setIsTeamTypesExpanded(true);
						createAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.jsmUseCaseExploreMoreClicked,
							attributes: {
								isJSMOnboarding: true,
								isServiceCollectionEnabled: true,
							},
						}).fire();
					}}
					aria-expanded={isTeamTypesExpanded}
					aria-label={`+ ${showMoreText}`}
					type="button"
				>
					<AddIcon label="" color="currentColor" />
					<span>{showMoreText}</span>
				</button>
			)}
		</Box>
	);
};

const revealCardKeyframes = keyframes({
	from: {
		opacity: 0,
		transform: 'translateY(-8px)',
	},
	to: {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const revealCardStyles = css({
	animation: `${revealCardKeyframes} 200ms ease-out both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const contentWrapperStyles = xcss({
	width: '100%',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
	maxWidth: '744px',
});

const gridContainerBase = xcss({
	width: '100%',
	boxSizing: 'border-box',
	paddingBlockStart: 'space.025',
	paddingBlockEnd: 'space.025',
	paddingInlineStart: 'space.025',
	paddingInlineEnd: 'space.025',
	gridTemplateColumns: '1fr',
	[media.above.sm]: {
		gridTemplateColumns: '1fr 1fr',
	},
});

const showMoreButtonStyles = css({
	width: '100%',
	marginBlockStart: token('space.300'),
	paddingBlockStart: token('space.150'),
	paddingBlockEnd: token('space.150'),
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.200'),
	backgroundColor: token('color.background.neutral.subtle'),
	border: `1px dashed ${token('color.border')}`,
	borderRadius: token('radius.large', '8px'),
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	columnGap: token('space.100'),
	color: token('color.text.subtle'),
	font: token('font.body'),
	fontWeight: token('font.weight.semibold'),
	textAlign: 'center',
	transition: 'background-color 150ms ease-out',
	':hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});
